import { useEffect } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import type { GongResponse } from '@/features/Setting/Integrations/integrationRedirection/gong';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getGongUrl } from '@/utils/integration';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { GongInfo } from '../types';
import { useGong } from './useGong';

export const GongItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    gongWorkspace,
    disconnectGongMutation,
    requestImportMutation,
    refetch,
  } = useGong(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<GongResponse | null>('gong-redirect', null);

  const importModalState = useModalState<{
    workspace?: GongInfo;
    isNew: boolean;
  }>();

  const handleConnect = () => {
    if (!currWorkspace) return;
    window.open(getGongUrl(currWorkspace.alias));
  };

  const handleDisconnect = () => {
    disconnectGongMutation();
  };

  const handleImportWorkspace = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    refetch();
    fetchFeedbackSources(currWorkspace.alias);
    importModalState.open({
      modalState: {
        isNew: true,
      },
    });
  }, [
    currWorkspace,
    fetchFeedbackSources,
    importModalState,
    isConnector,
    redirect,
    refetch,
    removeKey,
    setRedirect,
  ]);

  if (isConnector && gongWorkspace) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!gongWorkspace}
        source={SOURCE_TYPE.GONG}
        description="Import call transcript as feedback into Syncly"
        connectedDisplay={gongWorkspace?.name || 'Gong'}
        info={gongWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnect}
        additionalActions={(workspace: GongInfo) => (
          <div>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        {gongWorkspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState?.isNew || false}
            title={'Import Calls'}
            subTitle={'Gong'}
            historyList={gongWorkspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImportWorkspace({ start_date, end_date })
            }
          />
        )}
      </Modal>
    </>
  );
};
